import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useService } from '@old/hooks/use-service';
import { useGlobalStore } from '@old/global-store/use-store';
import { ToastAction } from '@old/ui/toast/toast-action';
import {
  organizationIntegrationsService,
  revokeOrganizationIntegrationService,
} from '@old/services/domain/base/organization/organization-integrations';
import { OrganizationIntegration } from '@old/services/domain/base/organization/organization-integrations.types';
import i18n from '../../../../libs/language';

/**
 * TIC-4249: integradores (p. ej. Nera) autorizados a consultar el estado de monitoreo de la organización
 * y a activar monitoreo satelital. La autorización nace con el consentimiento del registro (/go/nera) o la
 * da SiloReal; acá el administrador de la organización puede darla de baja.
 */
export function IntegratorsSection() {
  const { userOrganizationId } = useGlobalStore();
  const organizationId = userOrganizationId ?? 0;

  const [toRevoke, setToRevoke] = useState<OrganizationIntegration | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const {
    summary: integrations,
    isLoading,
    trigger: reload,
  } = useService(organizationIntegrationsService, { fetchOnMount: [{ query: organizationId }] });

  const { trigger: revoke, isLoading: isRevoking } = useService(revokeOrganizationIntegrationService, {
    onSuccess: () => {
      setToast({ message: i18n.t('app.integration_modal.integrators_deactivated'), severity: 'success' });
      setToRevoke(null);
      reload({ query: organizationId });
    },
    onError: (error) => {
      const forbidden = error?.response?.status === 403;
      setToast({
        message: i18n.t(
          forbidden
            ? 'app.integration_modal.integrators_forbidden'
            : 'app.integration_modal.integrators_error'
        ),
        severity: 'error',
      });
      setToRevoke(null);
    },
  });

  const active = (integrations ?? []).filter((integration) => integration.active);
  const revoked = (integrations ?? []).filter((integration) => !integration.active);

  const formatDate = (value: string | null) => (value ? new Date(value).toLocaleDateString() : '');
  const sourceLabel = (source: OrganizationIntegration['source']) =>
    source === 'REFERRAL'
      ? i18n.t('app.integration_modal.integrators_source_referral')
      : i18n.t('app.integration_modal.integrators_source_manual');

  const confirmRevoke = () => {
    if (!toRevoke) return;
    revoke({ query: { organizationId, grantId: toRevoke.id } });
  };

  return (
    <Box mt={3}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle1" fontWeight={500}>
        {i18n.t('app.integration_modal.integrators_title')}
      </Typography>
      <Typography variant="body2" sx={{ color: '#7a7a7a', mb: 1.5 }}>
        {i18n.t('app.integration_modal.integrators_subtitle')}
      </Typography>

      {isLoading && !integrations ? (
        <Stack alignItems="center" py={2}>
          <CircularProgress size={20} />
        </Stack>
      ) : active.length === 0 ? (
        <Typography variant="body2" sx={{ color: '#a7a7a7', fontStyle: 'italic' }}>
          {i18n.t('app.integration_modal.integrators_empty')}
        </Typography>
      ) : (
        <Stack gap={1}>
          {active.map((integration) => (
            <Stack
              key={integration.id}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
              sx={{ border: '1px solid #e6e6e6', borderRadius: 2, px: 1.5, py: 1 }}
            >
              <Box>
                <Typography fontWeight={500}>
                  {integration.client.name}
                  {integration.company ? ` · ${integration.company.name}` : ''}
                </Typography>
                <Stack direction="row" gap={0.5} flexWrap="wrap" mt={0.5} alignItems="center">
                  {integration.can_read && (
                    <Chip size="small" label={i18n.t('app.integration_modal.integrators_read')} />
                  )}
                  {integration.can_activate && (
                    <Chip size="small" label={i18n.t('app.integration_modal.integrators_activate')} />
                  )}
                  {!integration.can_read && !integration.can_activate && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={i18n.t('app.integration_modal.integrators_no_permissions')}
                    />
                  )}
                  <Typography variant="caption" sx={{ color: '#a7a7a7', ml: 0.5 }}>
                    {sourceLabel(integration.source)} · {i18n.t('app.integration_modal.integrators_since')}{' '}
                    {formatDate(integration.created_at)}
                  </Typography>
                </Stack>
              </Box>
              <Button
                variant="outlined"
                color="error"
                size="small"
                sx={{ textTransform: 'none', borderRadius: 100 }}
                onClick={() => setToRevoke(integration)}
                disabled={isRevoking}
              >
                {i18n.t('app.integration_modal.integrators_deactivate')}
              </Button>
            </Stack>
          ))}
        </Stack>
      )}

      {revoked.length > 0 && (
        <Typography variant="caption" sx={{ color: '#a7a7a7', display: 'block', mt: 1 }}>
          {revoked
            .map(
              (integration) =>
                `${integration.client.name}: ${i18n.t('app.integration_modal.integrators_revoked')} ${formatDate(
                  integration.revoked_at
                )}`
            )
            .join(' · ')}
        </Typography>
      )}

      <Dialog open={Boolean(toRevoke)} onClose={() => (isRevoking ? undefined : setToRevoke(null))}>
        <DialogTitle>{i18n.t('app.integration_modal.integrators_deactivate')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {i18n
              .t('app.integration_modal.integrators_deactivate_confirm')
              .replace('{client}', toRevoke?.client.name ?? '')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToRevoke(null)} disabled={isRevoking}>
            {i18n.t('app.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            disableElevation
            onClick={confirmRevoke}
            disabled={isRevoking}
          >
            {isRevoking ? <CircularProgress size={18} sx={{ color: 'white' }} /> : i18n.t('app.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {toast && (
        <ToastAction message={toast.message} severity={toast.severity} onClose={() => setToast(null)} />
      )}
    </Box>
  );
}
